import {
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteField,
  serverTimestamp,
  FieldPath,
} from "firebase/firestore"
import { db } from "../firebase"

// Firestore soft-limits sustained writes to ~1/sec per document;
// this also collapses rapid stepper taps into a single write.
const FLUSH_DELAY_MS = 1000
// failed flushes retry with exponential backoff, capped here
const MAX_RETRY_DELAY_MS = 30000
// logout waits this long for a final flush before proceeding anyway
// (offline, the write is journaled by the persistent cache and the
// ack never arrives — signOut must not hang on it)
const DISPOSE_FLUSH_TIMEOUT_MS = 2000

// The cloud copy only stores nonzero counts; the app backfills
// zeros client-side (updateInventoryWithLatest in CardsContext).
export function compactInventory(map) {
  const out = {}
  Object.entries(map ?? {}).forEach(([name, count]) => {
    if (count > 0) {
      out[name] = count
    }
  })
  return out
}

// Card names are arbitrary strings (commas today, maybe dots or
// anything tomorrow). String paths passed to updateDoc are parsed
// as dot-separated, so per-card updates MUST go through FieldPath,
// whose segments are literal. Keys inside plain data objects
// (setDoc / nested maps) are never parsed as paths and are safe.
export function createCloudSync(uid) {
  const ref = doc(db, "users", uid)

  let pendingInventory = {} // { [cardName]: absoluteCount }
  let pendingFields = {} // { ignored: [...] } (settings stay per-device)
  let timer = null
  let unsubscribeSnap = null
  let disposed = false
  let failures = 0

  function hasPending() {
    return (
      Object.keys(pendingInventory).length > 0 ||
      Object.keys(pendingFields).length > 0
    )
  }

  function armFlush(delay = FLUSH_DELAY_MS) {
    if (disposed) {
      return
    }
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(flush, delay)
  }

  async function flush() {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    if (!hasPending()) {
      return
    }
    const inventory = pendingInventory
    const fields = pendingFields
    pendingInventory = {}
    pendingFields = {}

    // put the failed deltas back (without clobbering anything queued
    // while the write was in flight) and retry with backoff, so a
    // transient error can't strand edits in memory forever
    const restoreAndRetry = (err) => {
      console.error("cloud-sync flush failed", err)
      pendingInventory = { ...inventory, ...pendingInventory }
      pendingFields = { ...fields, ...pendingFields }
      failures += 1
      armFlush(Math.min(MAX_RETRY_DELAY_MS, FLUSH_DELAY_MS * 2 ** failures))
    }

    const args = []
    Object.entries(inventory).forEach(([name, count]) => {
      args.push(new FieldPath("inventory", name), count > 0 ? count : deleteField())
    })
    Object.entries(fields).forEach(([key, value]) => {
      args.push(new FieldPath(key), value)
    })
    args.push(new FieldPath("updatedAt"), serverTimestamp())

    try {
      await updateDoc(ref, ...args)
      failures = 0
    } catch (err) {
      // "not-found" can race the doc creation on a brand-new account;
      // fall back to a merge write (data-object keys are literal, so
      // card names are still safe, and deep-merging the inventory map
      // is exactly what per-card deltas want)
      if (err?.code === "not-found") {
        const data = { inventory: {}, updatedAt: serverTimestamp() }
        Object.entries(inventory).forEach(([name, count]) => {
          data.inventory[name] = count > 0 ? count : deleteField()
        })
        Object.entries(fields).forEach(([key, value]) => {
          data[key] = value
        })
        try {
          await setDoc(ref, data, { merge: true })
          failures = 0
        } catch (fallbackErr) {
          restoreAndRetry(fallbackErr)
        }
      } else {
        restoreAndRetry(err)
      }
    }
  }

  // best-effort: don't lose the last edit if the tab closes inside
  // the debounce window (the persistent cache journals the write)
  function onHidden() {
    if (document.visibilityState === "hidden") {
      flush()
    }
  }
  document.addEventListener("visibilitychange", onHidden)
  window.addEventListener("beforeunload", flush)

  return {
    subscribe(onData) {
      unsubscribeSnap = onSnapshot(
        ref,
        (snap) => {
          // latency-compensated echoes of our own queued writes;
          // applying them back would fight in-flight local edits
          if (snap.metadata.hasPendingWrites) {
            return
          }
          if (!snap.exists()) {
            // brand-new account: create the doc (settings are
            // per-device and never stored here)
            setDoc(
              ref,
              {
                inventory: {},
                ignored: [],
                decks: {},
                collectionImported: false,
                migrationPromptDismissed: false,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
              { merge: true },
            ).catch((e) => console.error("cloud-sync doc create failed", e))
            return
          }
          onData(snap.data())
        },
        (err) => {
          // e.g. rules not deployed / offline: stay in local mode
          console.error("cloud-sync subscription failed", err)
        },
      )
    },

    queueInventory(changes) {
      Object.assign(pendingInventory, changes)
      armFlush()
    },

    queueField(key, value) {
      pendingFields[key] = value
      armFlush()
    },

    // overlays for incoming snapshots, so a server ack racing new
    // local edits can never revert them
    pendingInventory() {
      return { ...pendingInventory }
    },
    pendingFields() {
      return { ...pendingFields }
    },

    // the ONLY full-map write (CSV upload / migration accept both
    // commit a collection, so the imported flag rides along).
    // mergeFields replaces the listed fields WHOLESALE — a plain
    // {merge:true} would deep-merge the inventory map and union the
    // local and cloud collections instead of replacing — while still
    // creating the doc when it doesn't exist yet (new accounts).
    async replaceInventory(map) {
      pendingInventory = {}
      await setDoc(
        ref,
        {
          inventory: compactInventory(map),
          collectionImported: true,
          updatedAt: serverTimestamp(),
        },
        { mergeFields: ["inventory", "collectionImported", "updatedAt"] },
      )
    },

    async setMigrationDismissed() {
      await setDoc(
        ref,
        { migrationPromptDismissed: true, updatedAt: serverTimestamp() },
        { merge: true },
      )
    },

    async dispose({ flush: doFlush } = {}) {
      disposed = true
      if (unsubscribeSnap) {
        unsubscribeSnap()
        unsubscribeSnap = null
      }
      document.removeEventListener("visibilitychange", onHidden)
      window.removeEventListener("beforeunload", flush)
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      if (doFlush) {
        // offline, the updateDoc ack never arrives even though the
        // write is journaled — bound the wait so logout can't hang
        await Promise.race([
          flush(),
          new Promise((resolve) =>
            setTimeout(resolve, DISPOSE_FLUSH_TIMEOUT_MS),
          ),
        ])
      }
    },
  }
}
